import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { bindActionCreators } from "redux";
import jwt from "jwt-decode";
import api from "../../api";
import { isExpiredJwtMessage } from "../../utils/fetchUtil";
import { makeSelectUserAuth } from "../../entities/questionnaireAnswer/selectors";
import { logout } from "../../lib/providers/auth-provider/actions";
import { rememberPrescriberAccessDenied } from "../../utils/prescriber";
import { ACCESS_DENIED_EVENT, grantsSignature, userCan } from "../../utils/accessContext";

const UserContext = createContext(null);

const USER_GRAPH =
  "[companyDetails.[country.[stringResources.[translations(filterByCurrentLanguage).[language]]]], entityStatus, role, userAccessContexts.[accessContext]]";

/**
 * Returns the access error for a prescribing user without a company.
 * @param {object|null} payload - User payload from the API.
 * @returns {string|null}
 */
const getFetchedUserAccessError = (payload) => {
  if (!payload || !payload.role) {
    return null;
  }
  const role = payload.role.role;
  if ((role === "OWNER" || role === "MANAGER" || role === "PRESCRIBER") && !payload.company_id) {
    return "NO_COMPANY";
  }
  return null;
};

const Provider = ({ userAuth, logout, children }) => {
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  let isLoggedIn = useRef(false);
  let loggingOut = useRef(false);

  /**
   * Logs the user out and sends them to the login page.
   * @param {string} [reason] - USER_DISABLED, OWNER_DISABLED, or NO_COMPANY, when known.
   */
  const denyAccess = (reason) => {
    if (loggingOut.current) {
      return;
    }
    loggingOut.current = true;
    if (reason) {
      rememberPrescriberAccessDenied(reason);
    }
    logout();
  };

  const fetchUser = async (id, suppressLoading = false) => {
    if (!suppressLoading) setUserLoading(true);

    const response = await api.fetch(`/V2/user/${id}`, {
      withGraphFetched: USER_GRAPH,
    });

    // if response 401, wait 3 seconds and then retry (token might be refreshing)
    if (response.status === 401 && isLoggedIn.current) {
      let unauthorizedMessage = "";
      try {
        const unauthorizedPayload = await response.clone().json();
        unauthorizedMessage =
          unauthorizedPayload && unauthorizedPayload.message
            ? String(unauthorizedPayload.message)
            : "";
      } catch (err) {
        unauthorizedMessage = "";
      }
      if (isExpiredJwtMessage(unauthorizedMessage)) {
        if (!suppressLoading) setUserLoading(false);
        return null;
      }

      await new Promise((res) => setTimeout(res, 3000));
      const retryResponse = await api.fetch(`/V2/user/${id}`, {
        withGraphFetched: USER_GRAPH,
      });

      if (retryResponse.status === 401 || retryResponse.status === 403) {
        const retryError = await retryResponse.json().catch(() => null);
        if (!suppressLoading) setUserLoading(false);
        denyAccess(
          retryError && retryError.message === "NO_COMPANY"
            ? "NO_COMPANY"
            : retryError && retryError.message === "OWNER_DISABLED"
              ? "OWNER_DISABLED"
              : retryError && retryError.message === "USER_DISABLED"
                ? "USER_DISABLED"
                : null,
        );
        return null;
      }

      const retryPayload = await retryResponse.json();
      if (!suppressLoading) setUserLoading(false);
      if (!retryResponse.ok || !retryPayload) {
        throw new Error("Failed to fetch user");
      }
      if (retryPayload.entityStatus && retryPayload.entityStatus.status === "DISABLED") {
        denyAccess(
          retryPayload.role && retryPayload.role.role === "OWNER"
            ? "OWNER_DISABLED"
            : "USER_DISABLED",
        );
        return null;
      }
      const retryCompanyError = getFetchedUserAccessError(retryPayload);
      if (retryCompanyError) {
        denyAccess(retryCompanyError);
        return null;
      }
      return retryPayload;
    }

    const payload = await response.json();

    if (!suppressLoading) setUserLoading(false);

    if (
      response.status === 403 &&
      payload &&
      (payload.message === "USER_DISABLED" ||
        payload.message === "OWNER_DISABLED" ||
        payload.message === "NO_COMPANY")
    ) {
      denyAccess(payload.message);
      return null;
    }

    if (!response.ok || !payload) {
      throw new Error("Failed to fetch user");
    }

    if (payload.entityStatus && payload.entityStatus.status === "DISABLED") {
      denyAccess(
        payload.role && payload.role.role === "OWNER"
          ? "OWNER_DISABLED"
          : "USER_DISABLED",
      );
      return null;
    }
    const companyError = getFetchedUserAccessError(payload);
    if (companyError) {
      denyAccess(companyError);
      return null;
    }

    return payload;
  };

  useEffect(() => {
    (async () => {
      if (!userAuth || !userAuth.token) {
        isLoggedIn.current = false;
        setUser(null);
        setUserLoading(false);
        return;
      }

      loggingOut.current = false;
      isLoggedIn.current = true;

      try {
        const sub = jwt(userAuth.token).sub;
        const fetchedUser = await fetchUser(sub, true);
        if (isLoggedIn.current) {
          setUser(fetchedUser);
          setUserLoading(false);
        }
      } catch (err) {
        if (isLoggedIn.current) {
          setUser(null);
          setUserLoading(false);
          denyAccess();
        }
      }
    })();
  }, [userAuth]);

  const assistantId =
    user && user.role && user.role.role === "ASSISTANT" ? user.id : null;

  useEffect(() => {
    if (!assistantId) {
      return undefined;
    }

    let cancelled = false;
    let interval = null;

    const refreshGrants = async () => {
      try {
        const refreshed = await fetchUser(assistantId, true);
        if (cancelled || !refreshed) {
          return;
        }
        setUser((current) => {
          if (
            !current ||
            current.id !== refreshed.id ||
            grantsSignature(current.userAccessContexts) ===
              grantsSignature(refreshed.userAccessContexts)
          ) {
            return current;
          }
          return refreshed;
        });
      } catch (err) {
        return;
      }
    };

    const start = () => {
      if (interval || cancelled) {
        return;
      }
      refreshGrants();
      interval = setInterval(refreshGrants, 10000);
    };

    const stop = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        stop();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener(ACCESS_DENIED_EVENT, refreshGrants);
    if (document.visibilityState === "visible") {
      start();
    }

    return () => {
      cancelled = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener(ACCESS_DENIED_EVENT, refreshGrants);
    };
  }, [assistantId]);

  const updateUser = async (newData) => {
    setUserLoading(true);

    try {
      const updatedUser = await api.fetch(`/V2/user/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newData),
      });

      const payload = await updatedUser.json();

      if (!updatedUser.ok || !payload) {
        throw new Error("Failed to update user");
      }

      setUser(payload);
      return true;
    } catch (err) {
      return false;
    } finally {
      setUserLoading(false);
    }
  };

  const updateOwnProfile = async (newData) => {
    const response = await api.fetch("/V2/prescriber/profile", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: newData,
    });
    const payload = await response.json();

    if (!response.ok || !payload) {
      const error = new Error((payload && payload.message) || "error_general");
      throw error;
    }

    if (user && user.id) {
      const refreshed = await fetchUser(user.id, true);
      if (refreshed) {
        setUser(refreshed);
        return true;
      }
    }

    setUser((current) => ({ ...current, ...payload }));
    return true;
  };

  const updateCompanyDetails = async (newData) => {
    setUserLoading(true);

    try {
      const companyId = user.companyDetails && user.companyDetails.id;
      const updatedCompany = await api.fetch(`/V2/company/${companyId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newData),
      });

      const payload = await updatedCompany.json();

      if (!updatedCompany.ok || !payload) {
        throw new Error("Failed to update company");
      }

      setUser((current) => ({ ...current, companyDetails: payload }));
      return true;
    } catch (err) {
      return false;
    } finally {
      setUserLoading(false);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        updateUser,
        updateOwnProfile,
        updateCompanyDetails,
        fetchUser,
        userLoading,
        can: (moduleName, action) => userCan(user, moduleName, action),
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within a UserProvider");
  return ctx;
};

const mapStateToProps = createStructuredSelector({
  userAuth: makeSelectUserAuth(),
});

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({ logout }, dispatch);
};

export const UserProvider = connect(mapStateToProps, mapDispatchToProps)(Provider);

export default UserContext;
